import { useState, useEffect } from 'react';
import api from '../../../api/client';

export default function WorkforcePlanPage() {
  const [models,   setModels]   = useState([]);
  const [versions, setVersions] = useState([]);
  const [rows,     setRows]     = useState([]);
  const [modelId,  setModelId]  = useState('');
  const [versionId,setVersionId]= useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [form,     setForm]     = useState({ period: '', plannedHeadcount: 0, plannedCostCents: 0 });
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    api.get('/planning/models').then(({ data }) => setModels(data.data?.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!modelId) { setVersions([]); return; }
    api.get(`/planning/versions?model_id=${modelId}`)
      .then(({ data }) => setVersions(data.data?.items || []))
      .catch(() => {});
  }, [modelId]);

  async function loadRows() {
    if (!versionId) { setRows([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/planning/workforce?version_id=${versionId}`);
      setRows(data.data?.items || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadRows(); }, [versionId]);

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/planning/workforce', {
        versionId,
        period:           form.period,
        plannedHeadcount: Number(form.plannedHeadcount),
        plannedCostCents: Number(form.plannedCostCents),
      });
      setForm({ period: '', plannedHeadcount: 0, plannedCostCents: 0 });
      await loadRows();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    try {
      await api.delete(`/planning/workforce/${id}`);
      await loadRows();
    } catch (e) { setError(e.message); }
  }

  return (
    <div data-testid="planning-workforce-page" data-loading={loading ? 'true' : 'false'}>
      <h2>Workforce Planning</h2>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <select
          data-testid="planning-workforce-model-select"
          value={modelId}
          onChange={(e) => { setModelId(e.target.value); setVersionId(''); }}
        >
          <option value="">— select model —</option>
          {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>

        <select
          data-testid="planning-workforce-version-select"
          value={versionId}
          onChange={(e) => setVersionId(e.target.value)}
          disabled={!modelId}
        >
          <option value="">— select version —</option>
          {versions.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.scenario})</option>)}
        </select>
      </div>

      {versionId && (
        <form onSubmit={handleCreate} style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <input
            data-testid="planning-workforce-period-input"
            type="month"
            value={form.period}
            onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))}
            required
          />
          <input
            data-testid="planning-workforce-headcount-input"
            type="number"
            min={0}
            value={form.plannedHeadcount}
            onChange={(e) => setForm((f) => ({ ...f, plannedHeadcount: e.target.value }))}
            placeholder="Planned headcount"
            style={{ width: '130px' }}
          />
          <input
            data-testid="planning-workforce-cost-input"
            type="number"
            min={0}
            value={form.plannedCostCents}
            onChange={(e) => setForm((f) => ({ ...f, plannedCostCents: e.target.value }))}
            placeholder="Planned cost (cents)"
            style={{ width: '160px' }}
          />
          <button type="submit" data-testid="planning-workforce-add" disabled={saving}>
            {saving ? 'Adding…' : 'Add Row'}
          </button>
        </form>
      )}

      {error && <p style={{ color: 'red' }}>{error}</p>}

      {loading ? <p>Loading…</p> : (
        <table data-testid="planning-workforce-list" style={{ width: '100%' }}>
          <thead>
            <tr><th>Period</th><th>Department</th><th>Planned HC</th><th>Actual HC</th><th>Cost (¢)</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const assumptions = typeof r.assumptions === 'object' ? r.assumptions : JSON.parse(r.assumptions || '{}');
              return (
                <tr key={r.id} data-testid={`planning-workforce-row-${r.id}`}>
                  <td>{r.period}</td>
                  <td>{r.department_name || '(all)'}</td>
                  <td style={{ textAlign: 'right' }}>{r.planned_headcount}</td>
                  <td style={{ textAlign: 'right' }}>{assumptions.actual_headcount ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>{Number(r.planned_cost_cents).toLocaleString()}</td>
                  <td>
                    <button
                      data-testid={`planning-workforce-delete-${r.id}`}
                      onClick={() => handleDelete(r.id)}
                    >Delete</button>
                  </td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={6}>No rows yet.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}
